import { useMsal } from '@azure/msal-react';
import { authConfig } from './authConfig';
import { useCallback, useEffect, useState } from 'react';

/**
 * Get a logout method
 * @returns a method that will log out the current active user and redirect to the home page
 * @group Authentication
 * @category Utils
 */
export const useMsalLogout = (): (() => void) => {
  const { instance } = useMsal();

  const onLogout = useCallback(() => {
    instance.logoutRedirect({
      account: instance.getActiveAccount(),
      postLogoutRedirectUri: '/logout', // Redirects to home page after logout
    });
  }, [instance]);

  return onLogout;
};

/**
 * Get a login method
 * @returns a method that will redirect to a login page
 * @group Authentication
 * @category Utils
 */
export function useLogin() {
  const { instance } = useMsal();

  const onLogin = useCallback(() => {
    instance.loginRedirect({
      scopes: authConfig.scopes,
    });
  }, [instance]);

  return { onLogin };
}

/**
 * get a logout method
 * @returns a method that will redirect to a login page
 * @group Authentication
 * @category Utils
 */
export function useLogout() {
  const { instance } = useMsal();

  return useCallback(() => instance.logoutRedirect(), [instance]);
}

/**
 * Get the access token and renew it if it's about to expire
 * @returns an access token if available and a loading flag
 * @group Authentication
 * @category Utils
 */
export function useToken() {
  const { instance } = useMsal();
  const { onLogin } = useLogin();

  const [accessToken, setAccessToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Function to acquire or renew token and then update state
  const  getToken = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const res = await instance.acquireTokenSilent({
        scopes: authConfig.scopes,
        forceRefresh
      });
      setAccessToken(res.accessToken);
    } catch (error) {
      onLogin();
    } finally {
      setIsLoading(false);
    }
  }, [instance, onLogin]);

  // Initial token acquisition
  useEffect(() => {
    if (!accessToken && !isLoading) {
      getToken();
    }
  }, [accessToken, isLoading, getToken]);

  useEffect(() => {
    if (!accessToken) return;
    
    const checkAndRenew = async () => {
      try {
        const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
        const expirationTime = tokenPayload.exp * 1000;
        const currentTime = Date.now();
        // If token expires in less than 5 minutes, renew it

        if (expirationTime - currentTime < 300000 || currentTime > expirationTime) {
          await getToken(true);
        }

      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    };

    // check if the token is expired
    checkAndRenew();

    const intervalId = setInterval(checkAndRenew, 60000); // check every minute
    return () => clearInterval(intervalId);
  }, [accessToken, getToken]);

  return { accessToken, isLoading: !accessToken && isLoading };
}
